// Canonical content for the two stays. Used to render the public pages
// (works even before the database is connected) and to seed the DB so
// bookings can reference each property by slug.
//
// 📸 To use your own photos: drop them in /public/images and update the
// `images` arrays below (first image is the cover).

export type Localized = { hy: string; ru: string; en: string };

// A free-form amenity with its own translations and a client-visibility flag.
export type CustomAmenity = Localized & { visible: boolean };

export type PropertyType = "APARTMENT" | "HOUSE";

export interface PropertyContent {
  slug: string;
  type: PropertyType;
  name: Localized;
  shortDesc: Localized;
  description: Localized;
  location: Localized;
  pricePerNight: number; // AMD
  maxGuests: number;
  bedrooms: number;
  beds: number;
  petsAllowed: boolean;
  smokingAllowed: boolean;
  amenities: string[]; // keys translated via the "Amenities" namespace
  customAmenities: CustomAmenity[]; // free-form amenities, per-language
  images: string[]; // paths under /public
  // Pricing summary derived from per-day overrides (set in properties-db). When
  // some upcoming nights differ from pricePerNight, `priceVaries` is true and
  // `priceFrom` is the lowest nightly price — shown as "from …" in listings.
  priceFrom?: number;
  priceVaries?: boolean;
  // Guest-review aggregate (0–10 average + count), attached from the DB.
  ratingScore?: number;
  ratingCount?: number;
}

export const properties: PropertyContent[] = [
  {
    slug: "dilijan-apartment",
    type: "APARTMENT",
    name: {
      en: "Apartment on Shahumyan Street",
      ru: "Квартира на улице Шаумяна",
      hy: "Բնակարան Շահումյան փողոցում",
    },
    shortDesc: {
      en: "Bright, modern apartment in the heart of town — walk to cafés and the old town.",
      ru: "Светлая современная квартира в центре города — рядом кафе и старый город.",
      hy: "Լուսավոր, ժամանակակից բնակարան քաղաքի սրտում՝ սրճարանների և հին քաղաքի կողքին.",
    },
    description: {
      en: "Comfortable Living: Welcome house in Dilijan offers a one-bedroom apartment with a balcony, washing machine, kitchen, and TV. The living room provides a relaxing space for guests.\n\nEssential Amenities: Guests enjoy free WiFi, ensuring connectivity throughout their stay. The apartment includes a bathroom with modern facilities.\n\nConvenient Location: Located on Shahumyan Street, the property is 46 square meters from Dilijan. Reception staff speak English and Russian, assisting guests with their needs.",
      ru: "Комфортное проживание: Welcome house в Дилижане предлагает однокомнатную квартиру с балконом, стиральной машиной, кухней и телевизором. Гостиная — уютное место для отдыха гостей.\n\nОсновные удобства: Гостям доступен бесплатный Wi-Fi на протяжении всего проживания. В квартире есть ванная комната с современным оснащением.\n\nУдобное расположение: Квартира площадью 46 кв. м расположена на улице Шаумяна в Дилижане. Персонал на ресепшене говорит на английском и русском и всегда готов помочь гостям.",
      hy: "Հարմարավետ կեցություն: Welcome house-ը Դիլիջանում առաջարկում է մեկ ննջասենյականոց բնակարան՝ պատշգամբով, լվացքի մեքենայով, խոհանոցով և հեռուստացույցով։ Հյուրասենյակը հանգստի հարմարավետ տարածք է հյուրերի համար։\n\nՀիմնական հարմարություններ: Հյուրերը վայելում են անվճար Wi-Fi՝ ողջ կեցության ընթացքում։ Բնակարանն ունի ժամանակակից սանհանգույց։\n\nՀարմար տեղակայություն: 46 քառ. մ բնակարանը գտնվում է Շահումյան փողոցում՝ Դիլիջանում։ Ընդունարանի անձնակազմը խոսում է անգլերեն և ռուսերեն՝ օգնելով հյուրերին։",
    },
    location: {
      en: "Shahumyan Street, Dilijan, Tavush, Armenia",
      ru: "улица Шаумяна, Дилижан, Тавуш, Армения",
      hy: "Շահումյան փողոց, Դիլիջան, Տավուշ, Հայաստան",
    },
    pricePerNight: 22000,
    maxGuests: 4,
    bedrooms: 1,
    beds: 2,
    petsAllowed: false,
    smokingAllowed: false,
    amenities: [
      "wifi",
      "kitchen",
      "heating",
      "tv",
      "washer",
      "balcony",
      "parking",
      "coffee",
    ],
    customAmenities: [],
    images: [
      "/images/apartment-1.svg",
      "/images/apartment-2.svg",
      "/images/apartment-3.svg",
    ],
  },
  {
    slug: "dilijan-house",
    type: "HOUSE",
    name: {
      en: "House on Kalinin Street",
      ru: "Дом на улице Калинина",
      hy: "Տուն Կալինին փողոցում",
    },
    shortDesc: {
      en: "A peaceful private house surrounded by forest, with a garden and mountain views.",
      ru: "Тихий частный дом в окружении леса, с садом и видом на горы.",
      hy: "Հանգիստ առանձնատուն անտառի մեջ՝ այգով և լեռների տեսարանով.",
    },
    description: {
      en: "Escape to a spacious family house wrapped in Dilijan's green hills. Gather around the fireplace, cook in the full kitchen, and breathe the mountain air from the garden. Ideal for families and groups who want quiet and nature.",
      ru: "Сбегите в просторный семейный дом среди зелёных холмов Дилижана. Соберитесь у камина, готовьте на полноценной кухне и дышите горным воздухом в саду. Идеально для семей и компаний, ищущих тишину и природу.",
      hy: "Փախեք դեպի ընդարձ ընտանեկան տուն՝ Դիլիջանի կանաչ բլուրների մեջ։ Հավաքվեք բուխարու շուրջ, պատրաստեք լիարժեք խոհանոցում և շնչեք լեռնային օդը այգուց։ Իդեալական է ընտանիքների և խմբերի համար, ովքեր փնտրում են անդորր և բնություն։",
    },
    location: {
      en: "Kalinin Street, Dilijan, Tavush, Armenia",
      ru: "улица Калинина, Дилижан, Тавуш, Армения",
      hy: "Կալինին փողոց, Դիլիջան, Տավուշ, Հայաստան",
    },
    pricePerNight: 38000,
    maxGuests: 8,
    bedrooms: 2,
    beds: 4,
    petsAllowed: true,
    smokingAllowed: false,
    amenities: [
      "wifi",
      "kitchen",
      "heating",
      "fireplace",
      "garden",
      "mountainView",
      "parking",
      "petFriendly",
      "washer",
      "tv",
    ],
    customAmenities: [],
    images: [
      "/images/house-1.svg",
      "/images/house-2.svg",
      "/images/house-3.svg",
    ],
  },
];

export function getProperty(slug: string): PropertyContent | undefined {
  return properties.find((p) => p.slug === slug);
}

// Bundled placeholder shown when a stay's photo can't be loaded (e.g. an
// admin upload that exists only on the production disk, viewed locally).
// Uses the stay's static seed image, which always ships in /public/images.
const GENERIC_FALLBACK_IMAGE = "/images/apartment-1.svg";

export function fallbackImage(slug: string): string {
  return getProperty(slug)?.images[0] ?? GENERIC_FALLBACK_IMAGE;
}

export function localized(value: Localized, locale: string): string {
  return value[locale as keyof Localized] ?? value.en;
}
